/**
 * InitialRoomSelect â with entry animation and dynamic arrow sizing.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { useCurrentPhase } from "@/state/selectors";
import { useGameStore } from "@/state/gameStore";
import { RoomVariant } from "@/types/state";
import {
    ARROW_GAP,
    RoomOption,
} from "@/components/screens/SplashScreen/components/IntroSequence/components/WelcomeSequence/components/RoomOption";
import { RoomMaskDefs } from "@/components/screens/SplashScreen/components/IntroSequence/components/WelcomeSequence/components/RoomMask";
import { RoomTransitionOverlay } from "@/components/screens/SplashScreen/components/IntroSequence/components/WelcomeSequence/components/RoomTransitionOverlay";
import { useVideoCacheStore } from "@/hooks/useVideoCacheStore";

const FADE_OUT_S = 0.3;
const ZOOM_S = 0.9;
const TRACE_DELAY = 0.2;
const TRACE_DURATION = 0.4;
const MIN_ARROW_HEIGHT = 40;
const SLIDE_TRANSITION_DURATION = 500;
const TOP_GAP = 32;

type Props = {
    isActive?: boolean;
    onContinue: (executeContinue: () => void) => void;
    onSelectionChange: (selected: boolean) => void;
    onTransitionStart: () => void;
};

export function InitialRoomSelect({ isActive = false, onContinue, onSelectionChange, onTransitionStart }: Props) {
    const [selectedRoom, setSelectedRoom] = useState<RoomVariant | null>(null);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [zoomActive, setZoomActive] = useState(false);
    const [startRect, setStartRect] = useState<DOMRect | null>(null);
    const [containerRect, setContainerRect] = useState<DOMRect | null>(null);
    const [traceStarted, setTraceStarted] = useState(false);
    const [imagesRevealed, setImagesRevealed] = useState(false);
    const [arrowHeight, setArrowHeight] = useState(MIN_ARROW_HEIGHT);
    const { preloadVideos } = useVideoCacheStore();

    const phase = useCurrentPhase();
    const selectRoom = useGameStore((state) => state.selectRoom);

    const containerRef = useRef<HTMLDivElement>(null);
    const middleRef = useRef<HTMLDivElement>(null);
    const optionsRowRef = useRef<HTMLDivElement>(null);
    const roomARef = useRef<HTMLDivElement>(null);
    const roomBRef = useRef<HTMLDivElement>(null);

    // Wait for slide transition to finish before starting trace
    useEffect(() => {
        if (!isActive || traceStarted) return;
        const timer = setTimeout(() => setTraceStarted(true), SLIDE_TRANSITION_DURATION);
        return () => clearTimeout(timer);
    }, [isActive, traceStarted]);

    // Measure available space for arrows
    useLayoutEffect(() => {
        const measure = () => {
            if (!middleRef.current || !optionsRowRef.current) return;
            const middleH = middleRef.current.clientHeight;
            const optionsH = optionsRowRef.current.clientHeight;
            const available = (middleH - optionsH) / 2 - ARROW_GAP - TOP_GAP / 2;
            setArrowHeight(Math.max(MIN_ARROW_HEIGHT, available));
        };
        measure();
        const ro = new ResizeObserver(measure);
        if (middleRef.current) ro.observe(middleRef.current);
        return () => ro.disconnect();
    }, [phase]);

    const handleTraceComplete = useCallback(() => {
        setImagesRevealed(true);
    }, []);

    const handleContinue = useCallback(async () => {
        if (!selectedRoom) return;

        const ref = selectedRoom === "a" ? roomARef : roomBRef;
        const imgContainer = ref.current?.querySelector("button > div") as HTMLElement | null;
        if (!imgContainer) return;

        const measuredStartRect = imgContainer.getBoundingClientRect();
        const measuredContainerRect = containerRef.current?.getBoundingClientRect();
        if (!measuredContainerRect) return;

        setStartRect(measuredStartRect);
        setContainerRect(measuredContainerRect);

        onTransitionStart();
        setIsTransitioning(true);
        await delay(FADE_OUT_S * 1000);

        setZoomActive(true);
    }, [selectedRoom, onTransitionStart]);

    useEffect(() => {
        if (selectedRoom) {
            onContinue(() => handleContinue());
        }
    }, [selectedRoom, onContinue, handleContinue]);

    useEffect(() => {
        onSelectionChange(selectedRoom !== null);
    }, [selectedRoom, onSelectionChange]);

    const handleTransitionComplete = useCallback(() => {
        if (selectedRoom) {
            selectRoom(selectedRoom);
        }
    }, [selectedRoom, selectRoom]);

    useEffect(() => {
        const imagesToPreload = [];
        if (phase?.room_a?.animated) imagesToPreload.push(phase.room_a.animated);
        if (phase?.room_b?.animated) imagesToPreload.push(phase.room_b.animated);
        if (imagesToPreload.length > 0) {
            preloadVideos(imagesToPreload);
        }
    }, []);

    if (!phase) {
        return (
            <div className="flex size-full items-center justify-center">
                <p className="text-gray-600">No phase selected</p>
            </div>
        );
    }

    const roomA = phase.room_a;
    const roomB = phase.room_b;
    const selectedRoomData = selectedRoom === "a" ? roomA : roomB;
    const noneSelected = selectedRoom === null;
    const showImages = imagesRevealed || isTransitioning;

    return (
        <div ref={containerRef} className="flex size-full flex-col items-center px-4 pb-34">
            <RoomMaskDefs />

            <motion.h1
                className="text-secondary-500 mt-10 leading-[135%] font-bold text-center"
                animate={{ opacity: isTransitioning ? 0 : 1 }}
                transition={{ duration: FADE_OUT_S }}
            >
                Die erste ist ganz einfach:
                <br />
                Wie soll dein Zimmer aussehen?
            </motion.h1>

            <div ref={middleRef} className="flex flex-1 w-full items-center justify-center overflow-visibles">
                <div ref={optionsRowRef} className="flex items-center justify-center gap-8">
                    {roomA && (
                        <motion.div
                            animate={{
                                opacity: isTransitioning && selectedRoom !== "a" ? 0 : 1,
                            }}
                            transition={{ duration: FADE_OUT_S }}
                        >
                            <RoomOption
                                ref={roomARef}
                                room={roomA}
                                fadeOutDuration={FADE_OUT_S}
                                isSelected={selectedRoom === "a"}
                                onSelect={() => !isTransitioning && imagesRevealed && setSelectedRoom("a")}
                                isTransitioning={isTransitioning}
                                direction="left"
                                showTopArrow={noneSelected}
                                showImage={showImages}
                                traceTopArrow={traceStarted}
                                traceDelay={TRACE_DELAY}
                                traceDuration={TRACE_DURATION}
                                onTraceComplete={handleTraceComplete}
                                arrowHeight={arrowHeight}
                            />
                        </motion.div>
                    )}

                    {roomB && (
                        <motion.div
                            animate={{
                                opacity: isTransitioning && selectedRoom !== "b" ? 0 : 1,
                            }}
                            transition={{ duration: FADE_OUT_S }}
                        >
                            <RoomOption
                                ref={roomBRef}
                                room={roomB}
                                fadeOutDuration={FADE_OUT_S}
                                isSelected={selectedRoom === "b"}
                                onSelect={() => !isTransitioning && imagesRevealed && setSelectedRoom("b")}
                                isTransitioning={isTransitioning}
                                direction="right"
                                showTopArrow={noneSelected}
                                showImage={showImages}
                                traceTopArrow={traceStarted}
                                traceDelay={TRACE_DELAY}
                                traceDuration={TRACE_DURATION}
                                arrowHeight={arrowHeight}
                            />
                        </motion.div>
                    )}
                </div>
            </div>

            {zoomActive &&
                startRect &&
                containerRect &&
                selectedRoomData &&
                containerRef.current &&
                createPortal(
                    <RoomTransitionOverlay
                        room={selectedRoomData}
                        startRect={startRect}
                        containerRect={containerRect}
                        durationS={ZOOM_S}
                        onComplete={handleTransitionComplete}
                    />,
                    containerRef.current,
                )}
        </div>
    );
}

function delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
